import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { 
  FaWrench, FaLaptopMedical, FaCog, FaBatteryFull, 
  FaUserTie, FaTools, FaCertificate, FaHandshake,
  FaPhoneAlt, FaStar, FaCheckCircle, FaArrowRight,
  FaWhatsapp, FaCalendarAlt, FaHeadset, FaDollarSign,
  FaCarBattery, FaGoogle, FaQuoteLeft, FaShieldAlt
} from 'react-icons/fa';
import { 
  MercedesLogo, BmwLogo, AudiLogo, VolkswagenLogo, 
  MiniLogo, ToyotaLogo, LexusLogo 
} from '../components/BrandLogos';
import PartnersSection from '../components/PartnersSection';

const Home = () => {
  const { t } = useLanguage();

  const whyChooseFeatures = [
    {
      icon: <FaUserTie size={36} />,
      title: 'EXPERIENCED TECHNICIANS',
      subtitle: '15+ Years Industry Experience'
    },
    {
      icon: <FaLaptopMedical size={36} />,
      title: 'ADVANCED DIAGNOSTIC EQUIPMENT',
      subtitle: 'Dealer-Level Diagnostic Tools'
    },
    {
      icon: <FaCertificate size={36} />,
      title: 'EUROPEAN VEHICLE SPECIALISTS',
      subtitle: 'BMW, Mercedes, Audi & VW Experts'
    },
    {
      icon: <FaCarBattery size={36} />,
      title: 'HYBRID VEHICLE EXPERTS',
      subtitle: 'Specialized Hybrid Diagnostics & Repairs'
    },
    {
      icon: <FaDollarSign size={36} />,
      title: 'TRANSPARENT PRICING',
      subtitle: 'No Hidden Charges'
    },
    {
      icon: <FaShieldAlt size={36} />,
      title: 'WARRANTY SUPPORT',
      subtitle: 'Professional After-Sales Service'
    }
  ];

  const brandLogos = [
    <MercedesLogo className="w-10 h-10 text-white" />,
    <BmwLogo className="w-10 h-10 text-white" />,
    <AudiLogo className="w-16 h-10 text-white" />,
    <VolkswagenLogo className="w-10 h-10 text-white" />,
    <MiniLogo className="w-14 h-10 text-white" />,
    <ToyotaLogo className="w-12 h-10 text-white" />,
    <LexusLogo className="w-14 h-10 text-white" />
  ];

  const services = [
    {
      icon: <FaWrench size={48} />,
      title: t('services.europeanRepair'),
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/v0rye9tu_Workshop2.jpeg'
    },
    {
      icon: <FaLaptopMedical size={48} />,
      title: t('services.diagnostics'),
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/44zt2kut_Workshop5.jpeg'
    },
    {
      icon: <FaCog size={48} />,
      title: t('services.maintenance'),
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/dfgwra1l_Workshop18.jpeg'
    },
    {
      icon: <FaBatteryFull size={48} />,
      title: t('services.hybrid'),
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/8uizlpnp_New%202.jpeg'
    }
  ];

  const reviews = [
    {
      name: 'Ahmad bin Hassan',
      rating: 5,
      comment: 'Professional service and honest advice. They diagnosed my BMW issue accurately and fixed it at a fair price.',
      vehicle: 'BMW 320i'
    },
    {
      name: 'Tan Wei Ming',
      rating: 5,
      comment: 'Excellent hybrid battery service. My Toyota Prius battery was repaired quickly and runs perfectly now.',
      vehicle: 'Toyota Prius'
    },
    {
      name: 'Raj Kumar',
      rating: 5,
      comment: 'Transparent pricing and professional technicians. They explained everything clearly before starting the work.',
      vehicle: 'Mercedes-Benz C200'
    }
  ];

  const repairCases = [
    {
      title: 'BMW X5 Transmission Overhaul',
      vehicle: 'BMW X5',
      issue: 'Transmission slipping and rough shifting',
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/paabcr14_Workshop7.jpg'
    },
    {
      title: 'Volvo XC90 Hybrid Inverter Repair',
      vehicle: 'Volvo XC90',
      issue: 'Hybrid inverter malfunction & repair',
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/x1kreg6m_Workshop8.jpg'
    },
    {
      title: 'Toyota Camry Hybrid Battery Replacement',
      vehicle: 'Toyota Camry',
      issue: 'Hybrid battery degradation and warning lights',
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/n5f3p6pk_Workshop9.jpg'
    },
    {
      title: 'Mercedes-Benz S400 Diagnostics',
      vehicle: 'Mercedes-Benz S400',
      issue: 'Engine warning light & system diagnostics',
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/44zt2kut_Workshop5.jpeg'
    },
    {
      title: 'BMW X1 Transmission Overhaul',
      vehicle: 'BMW X1',
      issue: 'Transmission system overhaul and repair',
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/v0rye9tu_Workshop2.jpeg'
    },
    {
      title: 'Toyota Camry Hybrid Battery Repair',
      vehicle: 'Toyota Camry',
      issue: 'Hybrid battery module repair service',
      image: 'https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/8uizlpnp_New%202.jpeg'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section - Dark Navy with Car Image */}
      <section className="relative bg-dark-navy overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-[600px] py-16">
            {/* Left Content */}
            <div className="relative z-10">
              <h1 className="font-cabinet font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.1] mb-6">
                EUROPEAN & HYBRID<br/>
                <span className="text-blue-400">VEHICLE SPECIALISTS</span>
              </h1>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-10 max-w-xl">
                Professional Diagnostics, Repair, Maintenance & Hybrid Battery Solutions
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mb-12">
                <Link 
                  to="/contact" 
                  data-testid="hero-cta-book"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 bg-royal-blue text-white font-cabinet font-bold text-sm tracking-wide hover:bg-[#002277] transition-colors uppercase"
                >
                  <FaCalendarAlt size={16} />
                  {t('hero.cta1')}
                </Link>
                <a 
                  href="https://wa.me/message/EFBOOUUK2AO2K1" 
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-whatsapp"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-white/40 text-white font-cabinet font-bold text-sm tracking-wide hover:bg-white hover:text-dark-navy transition-colors uppercase"
                >
                  <FaWhatsapp size={16} />
                  {t('hero.cta2')}
                </a>
                <a 
                  href="tel:+6069513317" 
                  data-testid="hero-cta-call"
                  className="flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-white/40 text-white font-cabinet font-bold text-sm tracking-wide hover:bg-white hover:text-dark-navy transition-colors uppercase"
                >
                  <FaPhoneAlt size={14} />
                  {t('hero.cta3')}
                </a>
              </div>

              {/* Brand Logos Row */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4 opacity-70" data-testid="hero-brand-logos">
                {brandLogos.map((logo, index) => (
                  <div key={index} className="flex items-center justify-center" data-testid={`hero-brand-${index}`}>
                    {logo}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Image - Workshop */}
            <div className="relative hidden lg:block">
              <div className="absolute -right-32 top-1/2 -translate-y-1/2 w-[800px] h-[500px]">
                <img 
                  src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/0s9av5ex_Workshop10.jpg"
                  alt="Leng Kee Auto Workshop - European Vehicle Service Bays"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-dark-navy/40"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile workshop image */}
        <div className="lg:hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/0s9av5ex_Workshop10.jpg"
            alt="Leng Kee Auto Workshop"
            className="w-full h-64 object-cover"
          />
        </div>
      </section>

      {/* Engine Maintenance Featured Section */}
      <section className="bg-dark-navy py-20" data-testid="engine-maintenance-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2">
              <h2 className="font-cabinet font-extrabold text-3xl sm:text-4xl text-white mb-6 tracking-tight">
                {t('engineSection.title')}
              </h2>
              <p className="text-gray-300 text-base leading-relaxed mb-8">
                {t('engineSection.description')}
              </p>
              <ul className="space-y-3 mb-10">
                {[1, 2, 3, 4, 5].map(num => (
                  <li key={num} className="flex items-center text-white">
                    <FaCheckCircle className="text-blue-400 mr-3 flex-shrink-0" size={18} />
                    <span className="text-base">{t(`engineSection.item${num}`)}</span>
                  </li>
                ))}
              </ul>
              <Link 
                to="/services"
                data-testid="engine-learn-more-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-royal-blue text-white font-cabinet font-bold text-sm tracking-wide hover:bg-[#002277] transition-colors uppercase"
              >
                {t('engineSection.learnMore')}
                <FaArrowRight size={14} />
              </Link>
            </div>

            <div className="lg:col-span-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 h-[500px]">
                <div className="overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/v0rye9tu_Workshop2.jpeg" 
                    alt="BMW X5 vehicle on hoist during repair"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/44zt2kut_Workshop5.jpeg" 
                    alt="Advanced diagnostics on Mercedes-Benz"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/fyzgh57f_Workshop3.jpeg" 
                    alt="Workshop overview showing multiple service bays"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/8uizlpnp_New%202.jpeg" 
                    alt="Workshop operations and vehicle maintenance area"
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar - 6 Key Benefits */}
      <section className="bg-[#0F1A3E] py-16 border-t border-white/10" data-testid="features-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {whyChooseFeatures.map((feature, index) => (
              <div 
                key={index}
                data-testid={`feature-bar-${index}`}
                className="flex flex-col items-center text-center"
              >
                <div className="text-blue-400 mb-3">
                  {feature.icon}
                </div>
                <h3 className="font-cabinet font-bold text-xs sm:text-sm text-white tracking-wide uppercase leading-tight mb-2">
                  {feature.title}
                </h3>
                <p className="text-xs text-gray-400 leading-tight">
                  {feature.subtitle}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workshop Scale Showcase */}
      <section className="py-24 bg-white" data-testid="workshop-showcase">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-royal-blue font-semibold text-sm tracking-widest uppercase mb-4">
                Our Workshop
              </div>
              <h2 className="font-cabinet font-bold text-3xl sm:text-4xl text-dark-navy mb-6 tracking-tight">
                A Large-Scale Professional Workshop
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Leng Kee Auto Service Centre operates a spacious, well-equipped workshop in Muar, Johor with multiple service bays designed to handle European and hybrid vehicles efficiently. Our facility is equipped with dealer-level diagnostic tools and specialized equipment for hybrid battery service.
              </p>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div>
                  <div className="font-cabinet font-extrabold text-3xl text-royal-blue mb-1">15+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Years Experience</div>
                </div>
                <div>
                  <div className="font-cabinet font-extrabold text-3xl text-royal-blue mb-1">8+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Service Bays</div>
                </div>
                <div>
                  <div className="font-cabinet font-extrabold text-3xl text-royal-blue mb-1">1000+</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Vehicles Serviced</div>
                </div>
                <div>
                  <div className="font-cabinet font-extrabold text-3xl text-royal-blue mb-1">10</div>
                  <div className="text-sm text-gray-600 uppercase tracking-wide">Brands Supported</div>
                </div>
              </div>
              <Link 
                to="/about"
                data-testid="learn-about-us-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-royal-blue text-white font-cabinet font-bold text-sm tracking-wide hover:bg-[#002277] transition-colors uppercase"
              >
                Learn About Us
                <FaArrowRight size={14} />
              </Link>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/2n9vqk2w_Workshop%20Front.png" 
                alt="Leng Kee Auto Service Centre Workshop Storefront"
                className="w-full h-[500px] object-cover border border-gray-200 shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section 
        className="py-24 relative bg-cover bg-center bg-fixed" 
        style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_premium-workshop-1/artifacts/iz72ui16_Workshop17.jpeg)' }}
        data-testid="services-overview"
      >
        <div className="absolute inset-0 bg-dark-navy/85"></div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="text-blue-400 font-semibold text-sm tracking-widest uppercase mb-3">
              Our Services
            </div>
            <h2 className="font-cabinet font-bold text-3xl sm:text-4xl text-white mb-4">
              Professional Automotive Services
            </h2>
            <p className="text-gray-300 text-base max-w-2xl mx-auto">
              Comprehensive automotive solutions for European luxury vehicles and hybrid cars
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                data-testid={`service-card-${index}`}
                className="group relative h-80 overflow-hidden border border-white/10 hover:shadow-2xl transition-shadow"
              >
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-navy via-dark-navy/50 to-transparent"></div>
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="text-blue-400 mb-4">
                    {service.icon}
                  </div>
                  <h3 className="font-cabinet font-bold text-xl text-white">
                    {service.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/services"
              data-testid="view-all-services-btn"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-white text-white font-cabinet font-bold hover:bg-white hover:text-dark-navy transition-colors uppercase tracking-wide"
            >
              View All Services
              <FaArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Recent Repair Cases */}
      <section className="py-24 bg-white" data-testid="repair-cases-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-royal-blue font-semibold text-sm tracking-widest uppercase mb-3">
              Case Studies
            </div>
            <h2 className="font-cabinet font-bold text-3xl sm:text-4xl text-dark-navy mb-4">
              Recent Repair Cases
            </h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">
              Real customer success stories from our workshop
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {repairCases.map((repairCase, index) => (
              <div 
                key={index}
                data-testid={`repair-case-${index}`}
                className="border border-gray-200 hover:shadow-lg transition-shadow overflow-hidden"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={repairCase.image} 
                    alt={repairCase.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-royal-blue text-white text-xs font-bold px-3 py-1 uppercase tracking-wide">
                    {repairCase.vehicle}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-cabinet font-bold text-lg text-dark-navy mb-3">
                    {repairCase.title}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start">
                      <span className="font-semibold text-royal-blue mr-2 flex-shrink-0">Issue:</span>
                      <span className="text-gray-600">{repairCase.issue}</span>
                    </div>
                    <div className="flex items-start">
                      <span className="font-semibold text-royal-blue mr-2 flex-shrink-0">Status:</span>
                      <span className="text-green-700 font-medium">✓ Successfully Repaired</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews / Testimonials */}
      <section className="py-24 bg-light-grey" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <FaGoogle className="text-blue-500" size={32} />
              <span className="font-cabinet font-bold text-2xl text-dark-navy">Google Reviews</span>
            </div>
            <div className="flex items-center justify-center gap-2 mb-3">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-yellow-400" size={24} />
              ))}
              <span className="ml-2 font-cabinet font-bold text-2xl text-dark-navy">3.9 / 5.0</span>
            </div>
            <p className="text-gray-600 text-base">Trusted by hundreds of customers in Muar and Johor</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div 
                key={index}
                data-testid={`testimonial-${index}`}
                className="bg-white p-8 border border-gray-200 hover:shadow-md transition-shadow relative"
              >
                <FaQuoteLeft className="absolute top-6 right-6 text-gray-200" size={32} />
                <div className="flex items-center mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400" size={18} />
                  ))}
                </div>
                <p className="text-gray-700 text-base leading-relaxed mb-6 italic">
                  "{review.comment}"
                </p>
                <div className="pt-4 border-t border-gray-200">
                  <p className="font-cabinet font-bold text-dark-navy">{review.name}</p>
                  <p className="text-sm text-gray-600 mt-1">{review.vehicle}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link 
              to="/reviews"
              data-testid="read-all-reviews-btn"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-royal-blue text-royal-blue font-cabinet font-bold hover:bg-royal-blue hover:text-white transition-colors uppercase tracking-wide"
            >
              Read More Reviews
              <FaArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Our Strategic Partners */}
      <PartnersSection />

      {/* Final CTA */}
      <section className="py-20 bg-royal-blue text-white" data-testid="final-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-cabinet font-extrabold text-3xl sm:text-4xl mb-4 tracking-tight">
            Ready to Service Your Vehicle?
          </h2>
          <p className="text-base mb-8 text-gray-200 max-w-2xl mx-auto">
            Contact us today for professional diagnostics, repair, and maintenance of your European or hybrid vehicle in Muar, Johor.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/message/EFBOOUUK2AO2K1"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
              className="flex items-center justify-center gap-2 px-8 py-3 bg-whatsapp text-white font-cabinet font-bold hover:bg-[#1EBE5A] transition-colors uppercase tracking-wide"
            >
              <FaWhatsapp size={18} />
              WhatsApp Us Now
            </a>
            <Link 
              to="/contact"
              data-testid="final-cta-contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white text-royal-blue font-cabinet font-bold hover:bg-gray-100 transition-colors uppercase tracking-wide"
            >
              <FaCalendarAlt size={16} />
              Book Appointment
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
